import React, { useEffect, useState, useSyncExternalStore } from 'react';

import { Box } from '@mui/material';
import { useRouter, useRouterState } from '@tanstack/react-router';

import { resolveDocumentTitle } from './title';

import type { AnyRouter, ParsedLocation } from '@tanstack/react-router';
import type { RefObject } from 'react';

interface Destination {
  href: string;
  key: string | undefined;
}

type Snapshot =
  | { kind: 'quiet'; generation: number }
  | { kind: 'probe'; generation: number; destination: Destination }
  | { kind: 'ready'; generation: number; destination: Destination; title: string };

interface Resolution {
  fromPathname: string;
  location: ParsedLocation;
  awaitingProbe: boolean;
}

interface DeliveredAnnouncement {
  snapshot: Extract<Snapshot, { kind: 'ready' }>;
  title: string;
}

const isCurrentNavigation = (
  snapshot: Snapshot,
  { location, resolvedLocation }: Pick<AnyRouter['state'], 'location' | 'resolvedLocation'>
): boolean => {
  if (snapshot.kind === 'quiet') return false;
  const matches = (value: ParsedLocation | undefined) =>
    value?.href === snapshot.destination.href && value.state.key === snapshot.destination.key;
  return matches(location) && matches(resolvedLocation);
};

class NavigationAccessibilityStore {
  private snapshot: Snapshot = { kind: 'quiet', generation: 0 };

  private focusedGeneration = 0;

  private listeners = new Set<() => void>();

  constructor(private readonly router: AnyRouter) {}

  getSnapshot = (): Snapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private publish(snapshot: Snapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }

  begin(): void {
    this.publish({ kind: 'quiet', generation: this.snapshot.generation + 1 });
  }

  resolve({ fromPathname, location, awaitingProbe }: Resolution): void {
    const generation = this.snapshot.generation + 1;
    if (fromPathname === location.pathname) {
      this.publish({ kind: 'quiet', generation });
      return;
    }
    const destination = { href: location.href, key: location.state.key };
    this.publish(
      awaitingProbe
        ? { kind: 'probe', generation, destination }
        : {
            kind: 'ready',
            generation,
            destination,
            title: resolveDocumentTitle(this.router) ? document.title : ''
          }
    );
  }

  isCurrent(snapshot: Snapshot): boolean {
    return isCurrentNavigation(snapshot, this.router.state);
  }

  probeReady(): void {
    const { snapshot } = this;
    if (
      snapshot.kind === 'probe' &&
      this.isCurrent(snapshot) &&
      resolveDocumentTitle(this.router)
    ) {
      this.publish({ ...snapshot, kind: 'ready', title: document.title });
    }
  }

  refreshTitle(): void {
    const { snapshot } = this;
    if (
      snapshot.kind === 'ready' &&
      this.isCurrent(snapshot) &&
      snapshot.title !== document.title
    ) {
      this.publish({ ...snapshot, title: document.title });
    }
  }

  consumeFocus(generation: number): boolean {
    if (this.focusedGeneration === generation) return false;
    this.focusedGeneration = generation;
    return true;
  }
}

const stores = new WeakMap<AnyRouter, NavigationAccessibilityStore>();

export const navigationAccessibility = (router: AnyRouter): NavigationAccessibilityStore => {
  let store = stores.get(router);
  if (!store) {
    store = new NavigationAccessibilityStore(router);
    stores.set(router, store);
  }
  return store;
};

const liveRegionStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};

const NavigationAccessibility = ({
  centerRef
}: {
  centerRef: RefObject<HTMLDivElement | null>;
}) => {
  const router = useRouter<AnyRouter>();
  const store = navigationAccessibility(router);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const current = useRouterState<AnyRouter, boolean>({
    select: (state) => isCurrentNavigation(snapshot, state)
  });
  const [delivery, setDelivery] = useState<DeliveredAnnouncement | null>(null);
  const announcement = delivery?.snapshot === snapshot ? delivery.title : '';

  useEffect(() => {
    const observer = new MutationObserver(() => store.refreshTitle());
    observer.observe(document.head, { childList: true, characterData: true, subtree: true });
    if (current) store.refreshTitle();
    return () => observer.disconnect();
  }, [current, store]);

  useEffect(() => {
    if (snapshot.kind === 'quiet' || !current) return undefined;
    const frame = requestAnimationFrame(() => {
      if (store.getSnapshot() !== snapshot || !store.isCurrent(snapshot)) return;
      if (centerRef.current && store.consumeFocus(snapshot.generation)) {
        centerRef.current.focus({ preventScroll: true });
      }
      if (snapshot.kind === 'ready') setDelivery({ snapshot, title: document.title });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [centerRef, current, snapshot, store]);

  return (
    <Box
      aria-atomic="true"
      aria-live="polite"
      component="span"
      id="single-root-navigation-announcement"
      sx={liveRegionStyles}
    >
      {announcement}
    </Box>
  );
};

export default NavigationAccessibility;
